import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { type ReactNode, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import {
  InGameCurrency,
  Rarity,
  RewardConfigRarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import {
  MonetaryRewardDisclaimer,
  MonetaryRewardDisclaimerNote,
} from '@sorare/core/src/components/jonum/MonetaryRewardDisclaimer';
import {
  ANY_SPORT_ESSENCE_TYPE,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_WHEEL,
  ANY_SPORT_SERIES_MARKET,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useWalletDrawerContext } from '@sorare/core/src/contexts/walletDrawer';
import { useGetMyCardsBoostLink } from '@sorare/core/src/hooks/my-cards/useGetMyCardsBoostLink';
import { useApplyJonumRestriction } from '@sorare/core/src/hooks/useApplyJonumRestriction';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { getRarityRGBColor } from '@sorare/core/src/lib/colors';
import { cryptocurrencySymbols } from '@sorare/core/src/lib/cryptocurrency';
import { fantasy, glossary, wheel } from '@sorare/core/src/lib/glossary';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';
import { getMonetaryAmountDisplayCurrency } from '@sorare/core/src/lib/monetaryAmount';
import {
  getRewardImage,
  isCraftingClue,
  mergeRewards,
  mergeRewardsConfig,
} from '@sorare/core/src/lib/rewards';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';
import { isGoldenWheelTicketCurrency } from '@sorare/core/src/lib/wheelCurrencies.ts';
import { RevealPackCardsDialogFromReward } from '@sorare/marketplace/src/components/commonCard/RevealPackCardsDialogFromReward';

import { DumbRewardUnlockedStory } from './DumbRewardUnlockedStory.tsx';
import { MonetaryRewardConversion } from './MonetaryRewardConversion/index.tsx';
import {
  MultiRewardUnlockedStory,
  type RewardGroupDisplayProps,
} from './MultiRewardUnlockedStory/index.tsx';
import type {
  RewardUnlockedStory_reward,
  RewardUnlockedStory_rewardConfig,
} from './__generated__/index.graphql.ts';
import { useOnCommonEssenceClick } from './useOnCommonEssenceClick.ts';

const messages = defineMessages({
  clueTitle: {
    defaultMessage: "You've gained crafting clue!",
  },
  defaultTitle: {
    defaultMessage: "You've won a reward!",
  },
  xpTitle: {
    defaultMessage: "You've gained {rarity} XP!",
  },
  mixedXpTitle: {
    defaultMessage: "You've gained XP!",
  },
  xpSubtitle: {
    defaultMessage: 'Level up your players with XP.',
  },
  essenceTitle: {
    defaultMessage: "You've won {rarity} Essence!",
  },
  essenceSubtitle: {
    defaultMessage: 'Use Essence to craft new players.',
  },
  essenceSubtitleCommon: {
    defaultMessage: 'Use Essence to buy new packs.',
  },
  essenceSubtitleProInSet: {
    defaultMessage: 'Use Essence to craft a NFT Pro Card',
  },
  monetaryTitle: {
    defaultMessage: "You've won cash!",
  },
  marketCreditTitle: {
    defaultMessage: "You've won market credits!",
  },
  marketCreditSubTitle: {
    defaultMessage: 'Use them in Auctions or Instant Buy',
  },
  raffleTicketTitle: {
    defaultMessage:
      "You've won {count, plural, one {a raffle ticket} other {raffle tickets}}!",
  },
  wheelTicketTitle: {
    defaultMessage:
      "You've won {rarity} {count, plural, one {wheel ticket} other {wheel tickets}}!",
  },
  goldenWheelTicketTitle: {
    defaultMessage:
      "You've won {rarity} {count, plural, one {golden wheel ticket} other {golden wheel tickets}}!",
  },
  wheelTicketSubtitle: {
    defaultMessage: 'Use them to spin the wheel.',
  },
  gemTitle: {
    defaultMessage: "You've won Gems!",
  },
  cardPackTitle: {
    defaultMessage: "You've won a pack",
  },
  cardTitle: {
    defaultMessage: "You've won a card!",
  },
  openPack: {
    defaultMessage: 'Open pack',
  },
  deliverableItemTitle: {
    defaultMessage: "You've won: {deliverableItemName}",
  },
});

type Props = {
  onClose: () => void;
  rewards?: RewardUnlockedStory_reward[];
  fallbackRewardConfigs?: RewardUnlockedStory_rewardConfig[];
  showContinueButton?: boolean;
  hideAction?: boolean;
  header?: ReactNode;
  useSetBackground?: boolean;
  hideBackground?: boolean;
};

type RewardItem = {
  reward?: RewardUnlockedStory_reward;
  config: RewardUnlockedStory_rewardConfig;
};

export const RewardUnlockedStory = ({
  onClose,
  rewards,
  fallbackRewardConfigs,
  showContinueButton = true,
  hideAction = false,
  header,
  useSetBackground,
  hideBackground,
}: Props) => {
  const [revealPackOpen, setRevealPackOpen] = useState(false);
  const { formatNumber } = useIntlContext();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();
  const { sport, generateSportContextPath } = useSportContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  const { formatMessage } = useIntl();
  const { fiatCurrency, cryptocurrency } = useCurrentUserContext();
  const { showWallet } = useWalletDrawerContext();
  const onCommonEssenceClick = useOnCommonEssenceClick(!showContinueButton);
  const showCryptoCopy = useApplyJonumRestriction();

  const items: RewardItem[] = rewards?.length
    ? rewards.map(reward => ({ reward, config: reward.config }))
    : (fallbackRewardConfigs ?? []).map(config => ({ config }));
  const rewardConfigs = items.map(i => i.config);
  const cardPackReward = rewards?.find(isTypeFn('CardPackReward'));

  const mergedRewardConfigs = mergeRewardsConfig(rewardConfigs);
  if (mergedRewardConfigs.length === 0) {
    return null;
  }

  const allImgUrls = items
    .map(i => getRewardImage(i.reward ?? i.config))
    .filter(Boolean);

  const rewardsGroupedByType = groupBy(items, i => i.config.__typename);
  const typeGroups = Object.values(rewardsGroupedByType).filter(
    (g): g is NonNullable<typeof g> => !!g
  );

  const hasCardPackRewardCta =
    !!cardPackReward &&
    mergedRewardConfigs.some(isTypeFn('CardPacksRewardConfig'));
  const effectiveShowContinueButton = hasCardPackRewardCta
    ? false
    : showContinueButton;

  const buttonColor = effectiveShowContinueButton ? 'secondary' : 'primary';

  const hasMonetaryReward = mergedRewardConfigs.some(
    isTypeFn('MonetaryRewardConfig')
  );

  const monetaryDisclaimer = hasMonetaryReward ? (
    <MonetaryRewardDisclaimerNote variant="monetary" />
  ) : null;

  const walletCta =
    showCryptoCopy && hasMonetaryReward ? (
      <Button
        size="large"
        color="primary"
        onClick={() => {
          onClose();
          showWallet();
        }}
      >
        <FormattedMessage defaultMessage="Go to my Wallet" />
      </Button>
    ) : null;

  // Secondary Continue kept below the primary wallet CTA — the story renders its own Continue on top.
  const walletCtaContinueButton =
    walletCta && effectiveShowContinueButton ? (
      <Button size="large" color="secondary" onClick={onClose} disableDebounce>
        <FormattedMessage {...glossary.continue} />
      </Button>
    ) : null;

  const getGroupDisplayProps = (
    groupItems: RewardItem[],
    imageSize?: 'big'
  ): RewardGroupDisplayProps | null => {
    const groupConfigs = groupItems.map(i => i.config);
    const merged = mergeRewardsConfig(groupConfigs);
    const first = merged.at(0);
    if (!first) return null;

    const groupRewards = groupItems
      .map(i => i.reward)
      .filter((r): r is NonNullable<typeof r> => !!r);
    const imgUrls = (
      groupRewards.length === groupItems.length
        ? mergeRewards(groupRewards)
        : merged
    )
      .map(rewardOrConfig => getRewardImage(rewardOrConfig, imageSize))
      .filter(Boolean);

    if (isType(first, 'InGameCurrencyRewardConfig')) {
      const inGameCurrencyConfigs = groupConfigs.filter(
        isTypeFn('InGameCurrencyRewardConfig')
      );
      const inGameCurrenciesGroupedByCurrency = groupBy(
        inGameCurrencyConfigs,
        r => r.currency
      );
      const rewardCountsByCurrency = Object.entries(
        inGameCurrenciesGroupedByCurrency
      ).reduce<Record<InGameCurrency, number>>(
        (acc, [currency, currencyConfigs]) => {
          acc[currency as InGameCurrency] = currencyConfigs.reduce<number>(
            (sum, curr) => sum + curr.amount,
            0
          );
          return acc;
        },
        {} as Record<InGameCurrency, number>
      );
      const rarityTyped =
        inGameCurrencyToRarity[
          Object.keys(rewardCountsByCurrency)[0] as InGameCurrency
        ];
      let amount: string;
      let title: ReactNode;
      let subtitle: string | undefined;
      let action: ReactNode;
      if (Object.keys(rewardCountsByCurrency).length === 1) {
        const currency = Object.keys(rewardCountsByCurrency).at(
          0
        ) as InGameCurrency;
        if (
          currency === InGameCurrency.COMMON_WHEEL_TICKET ||
          currency === InGameCurrency.WHEEL_TICKET
        ) {
          const rarity =
            currency === InGameCurrency.COMMON_WHEEL_TICKET
              ? Rarity.common
              : Rarity.limited;
          title = formatMessage(messages.wheelTicketTitle, {
            rarity: scarcityNames[rarity],
            count: Object.values(rewardCountsByCurrency).at(0),
          });
          subtitle = formatMessage(messages.wheelTicketSubtitle);
          action = (
            <Button
              size="large"
              to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL)}
              color={buttonColor}
            >
              <FormattedMessage {...wheel.spin} />
            </Button>
          );
        } else if (isGoldenWheelTicketCurrency(currency)) {
          title = formatMessage(messages.goldenWheelTicketTitle, {
            rarity:
              rarityTyped === Rarity.common
                ? // display nothing for common golden wheel ticket as common is not the right word in Sets
                  ''
                : scarcityNames[rarityTyped || Rarity.limited],
            count: Object.values(rewardCountsByCurrency).at(0),
          });
          subtitle = '';
          action = (
            <Button
              size="large"
              to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL)}
              color={buttonColor}
            >
              <FormattedMessage {...wheel.spin} />
            </Button>
          );
        } else if (isCraftingClue(currency)) {
          title = formatMessage(messages.clueTitle);
          subtitle = '';
          // No action button - show default "Continue" button
        } else if (
          InGameCurrency.COMMON_XP === currency ||
          InGameCurrency.LIMITED_XP === currency ||
          InGameCurrency.RARE_XP === currency ||
          InGameCurrency.SUPER_RARE_XP === currency ||
          InGameCurrency.UNIQUE_XP === currency
        ) {
          title = formatMessage(messages.xpTitle, {
            rarity: scarcityNames[rarityTyped || Rarity.common],
          });
          subtitle = formatMessage(messages.xpSubtitle);
          action = (
            <Button
              size="large"
              to={getMyCardsBoostLink({
                rarity: rarityTyped || Rarity.common,
                sport,
              })}
              color={buttonColor}
            >
              <FormattedMessage {...fantasy.levelUp} />
            </Button>
          );
        } else if (InGameCurrency.COMMON_GEM === currency) {
          title = formatMessage(messages.gemTitle);
          action = (
            <Button
              size="large"
              to={generateSportContextPath(ANY_SPORT_SERIES_MARKET)}
              color={buttonColor}
            >
              <FormattedMessage {...glossary.goToMarket} />
            </Button>
          );
        } else if (InGameCurrency.SET_RAFFLE_TICKET === currency) {
          title = formatMessage(messages.raffleTicketTitle, {
            count: Object.values(rewardCountsByCurrency).at(0),
          });
          subtitle = '';
        } else {
          title = formatMessage(messages.defaultTitle);
        }
        amount = formatNumber(Object.values(rewardCountsByCurrency)[0]);
      } else {
        title = formatMessage(messages.mixedXpTitle);
        amount = formatNumber(
          Object.values(rewardCountsByCurrency).reduce(
            (acc, curr) => acc + curr,
            0
          )
        );
      }
      return {
        title: title || <FormattedMessage {...messages.defaultTitle} />,
        subtitle,
        amount,
        action,
        rgbColor:
          Object.keys(rewardCountsByCurrency).length === 1 && rarityTyped
            ? getRarityRGBColor(rarityTyped)
            : undefined,
        imgUrls,
      };
    }

    if (isType(first, 'CardShardRewardConfig')) {
      const rarity = (
        first.configRarity !== RewardConfigRarity.unknown_rarity
          ? first.configRarity
          : Rarity.limited
      ) as Rarity;
      const isCommonPackEssence =
        first.configRarity === Rarity.common && sport !== Sport.BASEBALL;

      let action: ReactNode = (
        <Button
          to={generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
            params: { type: rarity.toLowerCase() },
          })}
          size="large"
          color={buttonColor}
        >
          <FormattedMessage defaultMessage="Craft players" />
        </Button>
      );

      if (isCommonPackEssence) {
        action = (
          <Button
            size="large"
            color={buttonColor}
            onClick={onCommonEssenceClick}
          >
            <FormattedMessage defaultMessage="Buy packs" />
          </Button>
        );
      } else if (useSetBackground) {
        action = (
          <Button
            to={generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
              params: { type: rarity.toLowerCase() },
            })}
            size="large"
            color={buttonColor}
          >
            <FormattedMessage defaultMessage="Get a Pro Card" />
          </Button>
        );
      }

      return {
        title: formatMessage(messages.essenceTitle, {
          rarity: first.cardSet?.name ?? scarcityNames[rarity],
        }),
        subtitle: formatMessage(
          isCommonPackEssence
            ? messages.essenceSubtitleCommon
            : useSetBackground
              ? messages.essenceSubtitleProInSet
              : messages.essenceSubtitle
        ),
        amount: formatNumber(first.quantity),
        rgbColor: getRarityRGBColor(rarity),
        action,
        imgUrls,
      };
    }

    if (isType(first, 'ConversionCreditRewardConfig')) {
      const { maxDiscount } = first;
      const usdAmount = formatNumber((maxDiscount.usdCents || 0) / 100, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });
      return {
        title: formatMessage(messages.marketCreditTitle),
        subtitle: formatMessage(messages.marketCreditSubTitle),
        amount: (
          <MonetaryRewardDisclaimer
            variant="conversionCredit"
            percentageDiscount={first.percentageDiscount}
          >
            {usdAmount}
          </MonetaryRewardDisclaimer>
        ),
        action: (
          <Button
            size="large"
            to={generateSportContextPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY)}
            color={buttonColor}
          >
            <FormattedMessage defaultMessage="Scout players" />
          </Button>
        ),
        rgbColor: '29, 60, 55',
        imgUrls,
      };
    }

    if (isType(first, 'CardPacksRewardConfig') && !!cardPackReward) {
      return {
        title: formatMessage(messages.cardPackTitle),
        rgbColor: getRarityRGBColor(Rarity.common),
        action: (
          <>
            <Button
              size="large"
              color={buttonColor}
              onClick={() => {
                setRevealPackOpen(true);
              }}
            >
              <FormattedMessage {...messages.openPack} />
            </Button>
            <RevealPackCardsDialogFromReward
              open={revealPackOpen}
              reward={cardPackReward}
              onClose={() => {
                setRevealPackOpen(false);
                onClose();
              }}
            />
          </>
        ),
        imgUrls,
      };
    }

    if (isType(first, 'MonetaryRewardConfig')) {
      const monetaryAmount = toMonetaryAmount(first.monetaryAmount);
      const displayCurrency = getMonetaryAmountDisplayCurrency(
        first.monetaryAmount,
        fiatCurrency
      );
      const totalCash = monetaryAmount.toMajorUnit(displayCurrency);
      const displayAmount =
        first.sharedPool && first.poolSize > 0
          ? totalCash / first.poolSize
          : totalCash;

      /* oxlint-disable sorare/enforce-monetary-reward-disclaimer --disclaimer is added around or not needed when we give the equivalent in crypto anyway */
      const formattedTotalCash = formatNumber(displayAmount, {
        style: 'currency',
        currency: displayCurrency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0,
      });
      return {
        title: showCryptoCopy
          ? formatMessage(
              {
                defaultMessage: "You've won {amount} worth of {crypto}!",
              },
              {
                amount: formattedTotalCash,
                crypto: cryptocurrencySymbols[cryptocurrency],
              }
            )
          : formatMessage(messages.monetaryTitle),
        amount: (
          <MonetaryRewardDisclaimer variant="monetary">
            {formattedTotalCash}
          </MonetaryRewardDisclaimer>
        ),
        rgbColor: '49, 42, 99',
        children: (
          <MonetaryRewardConversion
            displayAmount={displayAmount}
            displayCurrency={displayCurrency}
          />
        ),
        disclaimer: <MonetaryRewardDisclaimerNote variant="monetary" />,
        imgUrls,
      };
    }

    if (isType(first, 'CardRewardConfig')) {
      const cardReward = groupRewards.find(isTypeFn('AnyCardReward'));
      return {
        title: formatMessage(messages.cardTitle),
        subtitle: cardReward?.card.anyPlayer.displayName,
        rgbColor: getRarityRGBColor(first.rarity),
        imgUrls,
      };
    }

    if (isType(first, 'DeliverableItemRewardConfig')) {
      return {
        title: formatMessage(messages.deliverableItemTitle, {
          deliverableItemName: first.deliverableItem.name,
        }),
        imgUrls,
      };
    }

    return {
      title: <FormattedMessage {...messages.defaultTitle} />,
      imgUrls,
    };
  };

  if (typeGroups.length >= 2) {
    // on a set background, reveal monetary rewards last
    const orderedTypeGroups = useSetBackground
      ? [...typeGroups].sort(
          (a, b) =>
            Number(isType(a.at(0)?.config, 'MonetaryRewardConfig')) -
            Number(isType(b.at(0)?.config, 'MonetaryRewardConfig'))
        )
      : typeGroups;
    const groupsProps = orderedTypeGroups
      .map((group, index) =>
        getGroupDisplayProps(
          group,
          index === orderedTypeGroups.length - 1 ? 'big' : undefined
        )
      )
      .filter((props): props is RewardGroupDisplayProps => !!props);
    if (groupsProps.length === orderedTypeGroups.length) {
      const firstConfig = orderedTypeGroups[0].at(0)?.config;
      const isLimitedEssenceFirstOnSet =
        !!useSetBackground &&
        isType(firstConfig, 'CardShardRewardConfig') &&
        firstConfig.configRarity === RewardConfigRarity.limited;

      const lastScreenSetEssenceAction = isLimitedEssenceFirstOnSet ? (
        <>
          {walletCta}
          <Button
            size="large"
            color="secondary"
            to={generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
              params: { type: Rarity.limited.toLowerCase() },
            })}
          >
            <FormattedMessage defaultMessage="Craft on Pro" />
          </Button>
        </>
      ) : null;

      const lastGroupProps = groupsProps[groupsProps.length - 1];
      const baseLastGroupAction = hideAction
        ? undefined
        : lastGroupProps.action;
      const resolvedLastGroupAction =
        lastScreenSetEssenceAction ??
        (walletCta ? (
          <>
            {walletCta}
            {baseLastGroupAction}
            {walletCtaContinueButton}
          </>
        ) : (
          baseLastGroupAction
        ));

      return (
        <MultiRewardUnlockedStory
          header={header}
          onClose={onClose}
          groups={[
            ...groupsProps.slice(0, -1),
            { ...lastGroupProps, action: resolvedLastGroupAction },
          ]}
          showContinueButton={
            lastScreenSetEssenceAction || walletCta
              ? false
              : effectiveShowContinueButton
          }
          useSetBackground={useSetBackground}
          hideBackground={hideBackground}
        />
      );
    }
  }

  const props = getGroupDisplayProps(items);
  if (props) {
    const baseAction = hideAction ? undefined : props.action;
    const resolvedAction = walletCta ? (
      <>
        {walletCta}
        {baseAction}
        {walletCtaContinueButton}
      </>
    ) : (
      baseAction
    );
    return (
      <DumbRewardUnlockedStory
        onClose={onClose}
        header={header}
        imgUrls={allImgUrls}
        title={props.title}
        subtitle={props.subtitle}
        amount={props.amount}
        rgbColor={props.rgbColor}
        action={resolvedAction}
        showContinueButton={walletCta ? false : effectiveShowContinueButton}
        useSetBackground={useSetBackground}
        hideBackground={hideBackground}
        disclaimer={monetaryDisclaimer}
      >
        {props.children}
      </DumbRewardUnlockedStory>
    );
  }

  return (
    <DumbRewardUnlockedStory
      onClose={onClose}
      header={header}
      imgUrls={allImgUrls}
      showContinueButton={effectiveShowContinueButton}
      useSetBackground={useSetBackground}
      hideBackground={hideBackground}
    />
  );
};

const REWARD_CONFIG_FRAGMENT = gql`
  fragment RewardUnlockedStory_rewardConfig on AnyRewardConfigInterface {
    id
    ... on InGameCurrencyRewardConfig {
      id
      currency
      amount
    }
    ... on CardShardRewardConfig {
      id
      quantity
      configRarity
      cardSet {
        slug
        name
      }
    }
    ... on CardRewardConfig {
      id
      rarity
    }
    ... on MonetaryRewardConfig {
      id
      poolSize
      sharedPool
      monetaryAmount: amount {
        ...useMonetaryAmount_monetaryAmount
      }
    }
    ... on ConversionCreditRewardConfig {
      id
      percentageDiscount
      maxDiscount {
        ...useMonetaryAmount_monetaryAmount
      }
    }
    ... on DeliverableItemRewardConfig {
      id
      deliverableItem {
        slug
        name
      }
    }
    ...getRewardImage_anyRewardConfigInterface
    ...mergeRewardsConfig_anyRewardConfigInterface
  }
  ${useMonetaryAmount.fragments.monetaryAmount}
  ${getRewardImage.fragments.anyRewardConfigInterface}
  ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<RewardUnlockedStory_rewardConfig>;

RewardUnlockedStory.fragments = {
  anyRewardConfigInterface: REWARD_CONFIG_FRAGMENT,
  anyRewardInterface: gql`
    fragment RewardUnlockedStory_reward on AnyRewardInterface {
      id
      config {
        id
        ...RewardUnlockedStory_rewardConfig
      }
      ... on AnyCardReward {
        id
        card {
          slug
          anyPlayer {
            slug
            displayName
          }
        }
      }
      ...getRewardImage_anyRewardInterface
      ...mergeRewards_anyRewardInterface
      ...RevealPackCardsDialogFromReward_anyRewardInterface
    }
    ${REWARD_CONFIG_FRAGMENT}
    ${getRewardImage.fragments.anyRewardInterface}
    ${mergeRewards.fragments.AnyRewardInterface}
    ${RevealPackCardsDialogFromReward.fragments.anyRewardInterface}
  ` as TypedDocumentNode<RewardUnlockedStory_reward>,
};
